/**
 * BucketItemCard - Individual bucket list item card
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Heart, MapPin, Compass, Star, Target, MoreVertical, Edit, Trash2,
  CheckCircle, Circle, Lock, Share2, ListTodo, Camera, MessageCircle,
  BookOpen, Plane, DollarSign, Package, Instagram, PartyPopper, EyeOff
} from "lucide-react";
import { getCountryFlag } from "@/utils/countryFlags";
import confetti from 'canvas-confetti';
import { FoundingBadge } from "@/components/FoundingBadge";

const CATEGORIES = [
  { value: "travel", label: "Travel", icon: MapPin, color: "primary" },
  { value: "adventures", label: "Adventures", icon: Compass, color: "secondary" },
  { value: "experiences", label: "Experiences", icon: Star, color: "accent" },
  { value: "goals", label: "Goals", icon: Target, color: "charcoal" },
  { value: "romance", label: "Romance", icon: Heart, color: "coral" },
];

const getCategoryIcon = (category) => {
  const cat = CATEGORIES.find(c => c.value === category);
  return cat ? cat.icon : Star;
};

const getCategoryColor = (category) => {
  const colors = {
    travel: "category-travel",
    adventures: "category-adventures",
    experiences: "category-experiences",
    goals: "category-goals",
    romance: "category-romance",
  };
  return colors[category] || "category-experiences";
};

export const BucketItemCard = ({
  item,
  currentUserId,
  partnerId,
  onToggleComplete,
  onEdit,
  onDelete,
  onHide,
  onShare,
  onSummary,
  onItinerary,
  onTravelSearch,
  onBudget,
  onPhotos,
  onComments,
  onPacking,
  onSocialShare,
  rankColor = "from-cyan-500 to-teal-500", // Default gradient
  rankSolidColor = "bg-cyan-500", // Default solid color
  rankBorderColor = "border-cyan-500", // Default border color
}) => {
  const [showCelebration, setShowCelebration] = useState(false);
  const CategoryIcon = getCategoryIcon(item.category);
  const isOwner = item.created_by === currentUserId;
  // Partners can access features on each other's shared items
  const isPartnerItem = partnerId && item.created_by === partnerId && !item.is_private;
  const canAccessFeatures = isOwner || isPartnerItem;

  // Get country flag if available
  const countryFlag = getCountryFlag(item.country_code, item.country);

  // Handle completion with celebration
  const handleToggleComplete = () => {
    if (!item.completed) {
      // Trigger celebration
      setShowCelebration(true);
      
      // Fire confetti
      try {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6']
        });
      } catch (e) {
        // Confetti library might not be available
      }
      
      setTimeout(() => setShowCelebration(false), 2000);
    }
    onToggleComplete(item);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`bg-card rounded-2xl shadow-card overflow-hidden card-hover relative border ${
        item.completed 
          ? `border-l-4 ${rankBorderColor}` 
          : "border-charcoal/5"
      }`}
      data-testid={`bucket-item-${item.id}`}
    >
      {/* Completed overlay badge */}
      <AnimatePresence>
        {item.completed && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            className="absolute top-3 inset-x-0 z-10 flex justify-center"
          >
            <div className={`flex items-center gap-1 px-3 py-1 bg-gradient-to-r ${rankColor} text-white text-xs font-semibold rounded-full shadow-lg`}>
              <CheckCircle className="w-3.5 h-3.5" />
              COMPLETED
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Celebration animation */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="text-center"
            >
              <PartyPopper className="w-16 h-16 text-white mx-auto mb-2" />
              <p className="text-xl font-serif font-bold text-white">Dream Achieved!</p>
              <p className="text-sm text-white/80">Congratulations!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Priority indicator */}
      <div className={`priority-indicator ${item.priority}`} />
      
      <div className="p-6 pl-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className={`category-icon ${getCategoryColor(item.category)}`}>
            <CategoryIcon className="w-5 h-5" />
          </div>
          
          <div className="flex items-center gap-2">
            {item.is_private && (
              <Lock className="w-4 h-4 text-muted-foreground" />
            )}
            {item.completed && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onShare(item)}
                className="h-8 w-8 text-secondary hover:text-secondary hover:bg-secondary/10"
                data-testid={`share-item-${item.id}`}
              >
                <Share2 className="w-4 h-4" />
              </Button>
            )}
            {canAccessFeatures && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`item-menu-${item.id}`}>
                    <MoreVertical className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* Edit - only for owner */}
                  {isOwner && (
                    <DropdownMenuItem onClick={() => onEdit(item)} data-testid={`edit-item-${item.id}`}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {/* These features are available to both owner and partner */}
                  <DropdownMenuItem onClick={() => onSummary(item)} data-testid={`summary-item-${item.id}`}>
                    <BookOpen className="w-4 h-4 mr-2" />
                    View Summary
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onItinerary(item)} data-testid={`itinerary-item-${item.id}`}>
                    <ListTodo className="w-4 h-4 mr-2" />
                    Plan Itinerary
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onTravelSearch(item)} data-testid={`travel-search-item-${item.id}`}>
                    <Plane className="w-4 h-4 mr-2" />
                    Search Travel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onBudget(item)} data-testid={`budget-item-${item.id}`}>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Budget
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onPhotos(item)} data-testid={`photos-item-${item.id}`}>
                    <Camera className="w-4 h-4 mr-2" />
                    Photos
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onPacking(item)} data-testid={`packing-item-${item.id}`}>
                    <Package className="w-4 h-4 mr-2" />
                    Packing List
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onComments(item)} data-testid={`comments-item-${item.id}`}>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Comments
                  </DropdownMenuItem>
                  {/* Social Share - only for completed items */}
                  {item.completed && (
                    <DropdownMenuItem onClick={() => onSocialShare(item)} data-testid={`social-share-item-${item.id}`}>
                      <Instagram className="w-4 h-4 mr-2" />
                      Share to Social
                    </DropdownMenuItem>
                  )}
                  {/* Delete - only for owner */}
                  {isOwner && (
                    <DropdownMenuItem 
                      onClick={() => onDelete(item)}
                      className="text-destructive"
                      data-testid={`delete-item-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                  {/* Hide from my list - for items created by others (former partners) */}
                  {!isOwner && onHide && (
                    <DropdownMenuItem 
                      onClick={() => onHide(item)}
                      className="text-amber-600 dark:text-amber-500"
                      data-testid={`hide-item-${item.id}`}
                    >
                      <EyeOff className="w-4 h-4 mr-2" />
                      Hide from my list
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        
        {/* Content */}
        <h3 className={`font-serif text-lg font-medium text-foreground mb-2 ${item.completed ? "line-through" : ""}`}>
          {item.title}
        </h3>
        
        {item.description && (
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
            {item.description}
          </p>
        )}
        
        {/* Location with Country Flag */}
        {(item.location || item.country) && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            {countryFlag ? (
              <span className="text-lg" title={item.country}>{countryFlag}</span>
            ) : (
              <MapPin className="w-4 h-4" />
            )}
            <span>{item.location || item.country}</span>
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-charcoal/5">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              by {item.created_by_name}
              {item.founding_explorer && (
                <FoundingBadge 
                  size="xs" 
                  founderNumber={item.founding_explorer_number}
                  className="h-4 w-4"
                />
              )}
            </span>
          </div>
          
          <motion.button
            onClick={handleToggleComplete}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              item.completed
                ? `bg-gradient-to-r ${rankColor} text-white shadow-md`
                : "bg-muted text-foreground hover:bg-primary/10 hover:text-primary"
            }`}
            data-testid={`toggle-complete-${item.id}`}
          >
            {item.completed ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Achieved!
              </>
            ) : (
              <>
                <Circle className="w-4 h-4" />
                Mark Done
              </>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default BucketItemCard;
